import { Chip, Divider, Paper, Typography } from '@shared/ui';
import React from 'react';
import { registerWidgetRenderer } from '../registry';
import type { PlanningEntity, PlanningIterationDetails, TimelineWidgetProps } from '../types';

export interface PlanningWidgetProps extends TimelineWidgetProps<PlanningEntity> {
  debugMode?: boolean;
}

registerWidgetRenderer(
  'planning',
  ({ entity, debugMode }) => (
    <PlanningWidget key={entity.id} entity={entity as PlanningEntity} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

function renderIteration(iteration: PlanningIterationDetails): React.ReactNode {
  const rows: React.ReactNode[] = [];
  const meta: Array<[string, string | undefined]> = [
    ['Strategy', iteration.strategy],
    ['Progress', iteration.progress],
    ['Tool', iteration.toolName],
  ];
  meta.forEach(([label, value]) => {
    if (!value) return;
    rows.push(
      <Typography key={label} variant="body2" color="text.secondary">
        <strong>{label}:</strong> {value}
      </Typography>
    );
  });
  if (iteration.reasoning) {
    rows.push(
      <Typography key="reasoning" variant="body2" color="text.secondary">
        <strong>Reasoning:</strong> {iteration.reasoning}
      </Typography>
    );
  }
  if (iteration.reflectionText) {
    rows.push(
      <Typography key="reflection" variant="body2" color="text.secondary">
        <strong>Reflection:</strong> {iteration.reflectionText}
      </Typography>
    );
  }
  if (iteration.progressScore !== undefined) {
    rows.push(
      <Typography key="progressScore" variant="body2" color="text.secondary">
        <strong>Progress Score:</strong> {iteration.progressScore}
      </Typography>
    );
  }
  if (iteration.extra && Object.keys(iteration.extra).length > 0) {
    rows.push(
      <Typography key="extra" variant="body2" color="text.secondary">
        <strong>Extra:</strong> {JSON.stringify(iteration.extra)}
      </Typography>
    );
  }
  return rows;
}

export function PlanningWidget({ entity, debugMode = false }: PlanningWidgetProps) {
  const { props } = entity;
  const iterations = [...(props.iterations || [])].sort((a, b) => a.index - b.index);
  const statusColor =
    props.status === 'completed' ? 'success' : props.status === 'error' ? 'error' : 'primary';

  return (
    <Paper variant="outlined" className="p-4 bg-gradient-to-r from-sky-50 to-slate-50">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="subtitle1" fontWeight="bold">
            🧭 Planning Run
          </Typography>
          <Chip size="small" color={statusColor as any} label={props.status.toUpperCase()} />
          {props.provider ? <Chip size="small" variant="outlined" label={props.provider} /> : null}
          {props.plannerModel ? (
            <Chip size="small" variant="outlined" label={props.plannerModel} />
          ) : null}
          {props.maxIterations !== undefined ? (
            <Chip size="small" variant="outlined" label={`Limit ${props.maxIterations}`} />
          ) : null}
        </div>

        {props.finalDecision ? (
          <Typography variant="body2" color="text.secondary">
            Final Decision: <strong>{props.finalDecision}</strong>
            {props.statusReason ? ` · ${props.statusReason}` : ''}
          </Typography>
        ) : props.statusReason ? (
          <Typography variant="body2" color="text.secondary">
            {props.statusReason}
          </Typography>
        ) : null}

        {props.finalDirective ? (
          <Typography variant="body2" color="text.secondary">
            Directive: {props.finalDirective}
          </Typography>
        ) : null}

        {iterations.length > 0 ? (
          <div className="space-y-3">
            <Divider textAlign="left">Iterations ({iterations.length})</Divider>
            <div className="space-y-2">
              {iterations.map((iteration) => (
                <div
                  key={iteration.index}
                  className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                >
                  <Typography variant="subtitle2" className="mb-1">
                    Iteration {iteration.index}: {iteration.action || 'unknown'}
                  </Typography>
                  <div className="space-y-1">{renderIteration(iteration)}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {props.execution && props.execution.status !== 'idle' ? (
          <div className="space-y-2">
            <Divider textAlign="left">Execution</Divider>
            <Typography variant="body2" color="text.secondary">
              Status: <strong>{props.execution.status.toUpperCase()}</strong>
              {props.execution.statusText ? ` · ${props.execution.statusText}` : ''}
            </Typography>
            {props.execution.executorModel ? (
              <Typography variant="body2" color="text.secondary">
                Model: {props.execution.executorModel}
              </Typography>
            ) : null}
            {props.execution.directive ? (
              <Typography variant="body2" color="text.secondary">
                Directive: {props.execution.directive}
              </Typography>
            ) : null}
            {props.execution.tokensUsed !== undefined ? (
              <Typography variant="body2" color="text.secondary">
                Tokens Used: {props.execution.tokensUsed}
              </Typography>
            ) : null}
            {props.execution.responseLength !== undefined ? (
              <Typography variant="body2" color="text.secondary">
                Response Length: {props.execution.responseLength}
              </Typography>
            ) : null}
            {props.execution.extra && Object.keys(props.execution.extra).length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Extra: {JSON.stringify(props.execution.extra)}
              </Typography>
            ) : null}
            {props.execution.errorMessage ? (
              <Typography variant="body2" color="error">
                Error: {props.execution.errorMessage}
              </Typography>
            ) : null}
          </div>
        ) : null}

        {debugMode ? (
          <details className="bg-gray-100 rounded p-2">
            <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
            <pre className="text-xs mt-2 overflow-auto max-h-64">
              {JSON.stringify(props, null, 2)}
            </pre>
          </details>
        ) : null}
      </div>
    </Paper>
  );
}
